import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { ehNativo } from '@/lib/nativo';
import { cn } from '@/lib/utils';

// Distância (já amortecida) que dispara a atualização, e o máximo que o
// indicador desce.
const LIMIAR = 70;
const MAXIMO = 110;

// "Puxar para atualizar" do app: com a página no topo, arrastar para baixo
// mostra o indicador e, soltando depois do limiar, busca de novo os dados de
// todas as consultas da tela (React Query), sem recarregar o app. Fica fora
// quando há modal aberto (o body trava a rolagem) e dentro de áreas marcadas
// com data-sem-puxar (listas que rolam sozinhas).
export function PuxarParaAtualizar() {
  const queryClient = useQueryClient();
  const [distancia, setDistancia] = useState(0);
  const [atualizando, setAtualizando] = useState(false);
  const inicioY = useRef<number | null>(null);
  const distanciaRef = useRef(0);
  const atualizandoRef = useRef(false);

  useEffect(() => {
    // Sem isto o Chrome do Android faz o puxar dele por cima do nosso.
    const anterior = document.body.style.overscrollBehaviorY;
    document.body.style.overscrollBehaviorY = 'contain';

    const mudarDistancia = (valor: number) => {
      distanciaRef.current = valor;
      setDistancia(valor);
    };

    const aoTocar = (e: TouchEvent) => {
      if (atualizandoRef.current || e.touches.length !== 1) return;
      const alvo = e.target as Element | null;
      const modalAberto = document.body.style.overflow === 'hidden';
      if (window.scrollY > 0 || modalAberto || alvo?.closest('[data-sem-puxar], [role="dialog"]')) {
        inicioY.current = null;
        return;
      }
      inicioY.current = e.touches[0].clientY;
    };

    const aoMover = (e: TouchEvent) => {
      if (inicioY.current == null) return;
      const dy = e.touches[0].clientY - inicioY.current;
      if (dy <= 0 || window.scrollY > 0) {
        if (distanciaRef.current) mudarDistancia(0);
        return;
      }
      // Arrastar para baixo no topo é nosso: evita o "quique" da página.
      if (e.cancelable) e.preventDefault();
      mudarDistancia(Math.min(dy * 0.5, MAXIMO));
    };

    const aoSoltar = async () => {
      if (inicioY.current == null) return;
      inicioY.current = null;
      if (distanciaRef.current < LIMIAR) {
        mudarDistancia(0);
        return;
      }
      atualizandoRef.current = true;
      setAtualizando(true);
      mudarDistancia(LIMIAR);
      if (ehNativo) void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
      try {
        await queryClient.refetchQueries({ type: 'active' });
      } finally {
        atualizandoRef.current = false;
        setAtualizando(false);
        mudarDistancia(0);
      }
    };

    window.addEventListener('touchstart', aoTocar, { passive: true });
    window.addEventListener('touchmove', aoMover, { passive: false });
    window.addEventListener('touchend', aoSoltar);
    window.addEventListener('touchcancel', aoSoltar);
    return () => {
      document.body.style.overscrollBehaviorY = anterior;
      window.removeEventListener('touchstart', aoTocar);
      window.removeEventListener('touchmove', aoMover);
      window.removeEventListener('touchend', aoSoltar);
      window.removeEventListener('touchcancel', aoSoltar);
    };
  }, [queryClient]);

  if (!distancia && !atualizando) return null;

  const progresso = Math.min(distancia / LIMIAR, 1);
  const soltar = distancia >= LIMIAR;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center"
      style={{
        top: 'calc(env(safe-area-inset-top) + 3.5rem)',
        transform: `translateY(${distancia - 44}px)`,
        transition: inicioY.current == null ? 'transform 200ms ease-out' : undefined,
      }}
    >
      <span
        className={cn(
          'flex size-10 items-center justify-center rounded-full border border-border bg-card shadow-elevated',
          soltar || atualizando ? 'text-primary' : 'text-text-secondary'
        )}
        style={{ opacity: Math.max(progresso, 0.3) }}
      >
        <RefreshCw
          size={18}
          className={cn(atualizando && 'animate-spin')}
          style={atualizando ? undefined : { transform: `rotate(${progresso * 270}deg)` }}
        />
        <span className="sr-only">{atualizando ? 'Atualizando' : 'Puxe para atualizar'}</span>
      </span>
    </div>
  );
}
